import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Loader2, RefreshCw } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { supabase } from "@/lib/supabase";

const DEFAULT_VALUE = "__default__";

type ElAgent = { agent_id: string; name: string };

async function fetchElAgents(orgId: string, refresh: boolean): Promise<ElAgent[]> {
  const path = `concierge-list-agents?org_id=${encodeURIComponent(orgId)}${refresh ? "&refresh=1" : ""}`;
  const { data, error } = await supabase.functions.invoke(path, { method: "GET" });
  if (error) throw error;
  const list = Array.isArray((data as any)?.agents) ? (data as any).agents : [];
  return list as ElAgent[];
}

export function AgentPicker({
  orgId,
  value,
  onChange,
}: {
  orgId: string;
  value: string | null;
  onChange: (v: string | null) => void;
}) {
  const [refreshing, setRefreshing] = useState(false);
  const { data = [], isLoading, refetch } = useQuery({
    queryKey: ["concierge", "admin", "el-agents", orgId],
    queryFn: () => fetchElAgents(orgId, false),
    staleTime: 60_000,
  });

  const savedButMissing =
    value && !data.some((a) => a.agent_id === value) ? value : null;

  const onRefresh = async () => {
    setRefreshing(true);
    try {
      await fetchElAgents(orgId, true);
      await refetch();
    } finally {
      setRefreshing(false);
    }
  };

  return (
    <div className="flex items-center gap-2">
      <Select
        value={value ?? DEFAULT_VALUE}
        onValueChange={(v) => onChange(v === DEFAULT_VALUE ? null : v)}
      >
        <SelectTrigger className="flex-1">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={DEFAULT_VALUE}>Use default pinned agent</SelectItem>
          {isLoading && (
            <div className="flex items-center justify-center py-2">
              <Loader2 className="h-4 w-4 animate-spin opacity-60" />
            </div>
          )}
          {data.map((a) => (
            <SelectItem key={a.agent_id} value={a.agent_id}>
              {a.name}
              <span className="ml-2 text-xs text-muted-foreground">
                {a.agent_id.slice(0, 8)}…
              </span>
            </SelectItem>
          ))}
          {savedButMissing && (
            <SelectItem value={savedButMissing}>
              {savedButMissing} (not in list)
            </SelectItem>
          )}
        </SelectContent>
      </Select>
      <Button
        variant="outline"
        size="sm"
        onClick={onRefresh}
        disabled={refreshing}
        title="Refresh agent list from ElevenLabs"
      >
        <RefreshCw className={`h-4 w-4 ${refreshing ? "animate-spin" : ""}`} />
      </Button>
    </div>
  );
}

export default AgentPicker;
